import React, { useState, useEffect, useRef } from 'react';
import { TdsButton, TdsButtonContainer, TdsLoadingSpinner, TdsAlert } from '@trv-tds/react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import ValidatedField from '../../common/ValidatedField';
import { AUTO_SEARCH_TEXT, COMMON_TEXT } from '../../../constants/textConstants';
import { clearSearchResults, setExternalSearchType, clearError } from '../../../redux/slices/providerSearchSlice';
import type { RootState } from '../../../redux/store';
import type { Vendor } from '../../../types/providerSearch';
import type { FormConfig } from '../../../config/providerSearchConfig';
import { US_ZIP_CODE_REGEX, VALIDATION_MESSAGES } from '../../../constants/validationConstants';
import type { AppDispatch } from '../../../redux/store';
import { useProviderSearch } from '../../../hooks/useProviderSearch'
import { VENDOR_TYPE_SPECIALITY_MAP } from '../../../constants/providerSearchConstants';

// Generic form data interface
interface FormData {
  [key: string]: string | undefined;
}

export interface SearchFormProps {
  currentSearchCategory: 'auto' | 'property';
  formConfig: FormConfig;
  title?: string;
  description?: string;
  isExternalUser?: boolean;
  propertySpecialtyMap?: Record<string, Array<{ id: string; value: string }>>;
}

const SearchForm: React.FC<SearchFormProps> = ({
  currentSearchCategory,
  formConfig,
  isExternalUser,
  propertySpecialtyMap,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error } = useSelector((state: RootState) => state.providerSearch);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const errorAlertRef = useRef<HTMLDivElement>(null);
  const formRegionRef = useRef<HTMLDivElement>(null);
  const statusLiveRef = useRef<HTMLSpanElement>(null);
  const clearButtonRef = useRef<HTMLTdsButtonElement>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [dynamicSpecialityOptions, setDynamicSpecialityOptions] = useState(formConfig?.fields?.find(f => f.name === 'vendorSpecialty')?.options || []);
  const externalSearchType = useSelector((state: RootState) => state.providerSearch.externalSearchType);


  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    clearErrors,
    setError,
    trigger,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: formConfig.initialValues,
    mode: 'onSubmit'
  });

  // Reset form when initialValues change (e.g., when searchType changes)
  useEffect(() => {
    reset(formConfig.initialValues);
  }, [formConfig.initialValues, reset]);

  // Clear providerSearch error on mount and when navigating away
  useEffect(() => {
    dispatch(clearError());
    setShowErrorAlert(false);
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);

  // const [externalSearchType, setExternalSearchType] = useState<'local' | 'name'>('local');
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const [hasSubmittedOnce, setHasSubmittedOnce] = useState<boolean>(false);

  // Watch all form values for requiredEitherOr validation
  const formValues = watch();

  // Watch vendorType and update speciality options dynamically for property and contract vendors
  const watchedVendorType = watch('vendorType');

  useEffect(() => {
    // Base grouped options (with group) from the form config
    const baseGroupedOptions: Array<{ id: string; value: string; group?: string }> =
      formConfig?.fields?.find(f => f.name === 'vendorSpecialty')?.options || [];
    // Vendor type options (to map id -> display label used in group)
    const vendorTypeOptions: Array<{ id: string; value: string }> =
      formConfig?.fields?.find(f => f.name === 'vendorType')?.options || [];

    let newOptions: Array<{ id: string; value: string; group?: string }> = baseGroupedOptions;

    // If a vendor type is selected, filter the grouped options by allowed IDs from the map

    if (watchedVendorType) {
      // Determine the selected vendor type's display label (group label)
      const selectedVendorTypeLabel = (
        vendorTypeOptions.find(o => o.id === watchedVendorType || o.value === watchedVendorType)?.value
      ) || watchedVendorType;

      const allowed = (
        propertySpecialtyMap?.[watchedVendorType] ||
        propertySpecialtyMap?.[selectedVendorTypeLabel] ||
        VENDOR_TYPE_SPECIALITY_MAP[watchedVendorType] ||
        VENDOR_TYPE_SPECIALITY_MAP[selectedVendorTypeLabel] ||
        []
      )
        .filter(opt => opt.id && opt.id !== '')
        .map(opt => opt.id);
      const allowedSet = new Set(allowed);
      //// Revert grouping for Property: filter only by allowed IDs (ignore group label)
      newOptions = baseGroupedOptions.filter(
        opt => opt.id && allowedSet.has(opt.id)
      );
    }

    setDynamicSpecialityOptions(newOptions);
  }, [watchedVendorType, currentSearchCategory, propertySpecialtyMap, formConfig?.fields]);

  // --- PRIVILEGED USER LOGIC: Validation ---
  const validateForm = (data: FormData): boolean => {
    if (isExternalUser && currentSearchCategory === 'auto') {
      if (externalSearchType === 'name') {
        if (!data.vendorName?.trim()) {
          return false;
        }
      }
    }
    return true;
  };


  const { search, clearSearch } = useProviderSearch();
  const onSubmit = async (data: Record<string, string | undefined>) => {
    // React Hook Form validation occurs automatically before this function is called
    // Only perform additional custom validation here

    if (!validateForm(data)) {
      return;
    }

    // Convert undefined values to empty strings, and handle multiselect/multiselecttag as arrays
    const formData: Record<string, string | string[]> = {};
    Object.entries(data).forEach(([name, value]) => {
      formData[name] = value ?? '';
      dispatch({ type: 'providerSearch/updateField', payload: { name, value: formData[name] } });
    });

    // If property search and no vendorType selected, set vendorType to all allowed codes from options
    if (currentSearchCategory === 'property' && !formData.vendorType) {
      const vendorTypeOptions = formConfig?.fields?.find(f => f.name === 'vendorType')?.options;
      if (Array.isArray(vendorTypeOptions)) {
        formData.vendorType = vendorTypeOptions.map(opt => opt.id);
      }
    }

    // Use the custom hook to search
    search(formData, externalSearchType as 'local' | 'name' | 'type' | 'region', currentSearchCategory as 'auto' | 'property');
  };
  // Show/hide error alert based on slice error state
  useEffect(() => {
    if (error) {
      setShowErrorAlert(true);
      // Ensure user sees the alert at the top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Move focus to error alert for screen reader announcement
      setTimeout(() => {
        errorAlertRef.current?.focus();
      }, 0);
    } else {
      setShowErrorAlert(false);
    }
  }, [error]);

  // Custom submit handler that marks form as submitted before validation
  const handleFormSubmit = (e: React.MouseEvent<HTMLTdsButtonElement> | React.FormEvent) => {
    // Prevent default form submission behavior
    e.preventDefault();

    // Mark form as submitted immediately when submit is attempted
    setIsFormSubmitted(true);
    setHasSubmittedOnce(true);
    setStatusMessage('Submitting search…');
    
    // Let React Hook Form handle the rest (validation + onSubmit if valid)
    handleSubmit(onSubmit)(e as any);

  };

  const handleClearSearch = () => {
    reset();
    setIsFormSubmitted(false); // Reset form submission state
    clearSearch(); // Use the custom hook to clear search results
    setShowErrorAlert(false); // Hide any error alert when clearing search
    setStatusMessage('Search cleared.');
    // Keep focus on the Clear button after clearing
    setTimeout(() => {
      clearButtonRef.current?.focus();
    }, 0);
  };

  // Announce loading status changes politely for NVDA
  useEffect(() => {
    if (!hasSubmittedOnce) return;
    if (isLoading) {
      setStatusMessage('Searching…');
    } else {
      // Only announce completion after a submit
      setStatusMessage('Search complete.');
    }
  }, [isLoading, hasSubmittedOnce]);
  const genericRenderFields = () => {
    if (!formConfig?.rows || !formConfig?.fields) return null;

    return (<>
      {
        // Common external user rendering block
        isExternalUser && (
          <>
            {/* Render searchType field for external Users */}
            {formConfig.fields.map((field, rowIndex) => {
              if (field.name !== 'searchType') return null;
              const fieldConfig = field;
              return (
                <div className="tds-row tds-margin-bottom-large" key={fieldConfig.name}>
                  <ValidatedField
                    key={`${rowIndex}-${fieldConfig.name}`}
                    name={fieldConfig.name}
                    label={fieldConfig.label}
                    type={fieldConfig.type}
                    value={externalSearchType ?? ''}
                    onChangeCustom={(e: any) => {
                      const newValue = e.detail?.value || e.target?.value;
                      if (newValue !== externalSearchType) {
                        dispatch(setExternalSearchType(newValue));
                        setValue('searchType', newValue);
                        setIsFormSubmitted(false);
                        setShowErrorAlert(false);
                        dispatch(clearSearchResults());
                      }
                    }}
                    error={errors[fieldConfig.name] || undefined}
                    options={fieldConfig.options || []}
                    placeholder={fieldConfig.placeholder || ''}
                    disabled={fieldConfig.disabled || false}
                    register={register}
                    setValue={setValue}
                    clearErrors={clearErrors}
                    setError={setError}
                    validation={fieldConfig.validation}
                    limitations={fieldConfig.limitations}
                    colSize={fieldConfig.colSize || "12"}
                    formValues={formValues}
                    trigger={trigger}
                    isFormSubmitted={isFormSubmitted}
                    {...(fieldConfig.colSizeSm && { colSizeSm: fieldConfig.colSizeSm })}
                    {...(fieldConfig.colSizeMd && { colSizeMd: fieldConfig.colSizeMd })}
                    {...(fieldConfig.colSizeLg && { colSizeLg: fieldConfig.colSizeLg })}
                  />
                </div>
              );
            })}
            {/* Render Help Text for external Search */}
            {formConfig.helpText && (
                <p id="search-help-text" role="note" className="tds-small tds-margin-top-xl tds-margin-bottom-medium">
                  {formConfig.helpText}
                </p>
            )}
          </>
        )
      }
      {/* Render all General fields */}
      {formConfig.rows.map((row, rowIndex) => (
        <div key={rowIndex} className="tds-row">
          {row.map((fieldName) => {
            const fieldConfig = formConfig.fields.find(f => f.name === fieldName);
            if (!fieldConfig) return null;

            // Use dynamic options for vendorSpecialty if in property or contract category
            const fieldOptions = ((currentSearchCategory === 'property') && fieldName === 'vendorSpecialty')
              ? dynamicSpecialityOptions
              : fieldConfig.options || [];

            // vendorSpecialty field is always enabled now
            const isDisabled = fieldConfig.disabled || false;

            // Special handling for select fields (state and networkSelection)
            const handleFieldChange = (fieldConfig.type === 'select') ?
              (e: any) => {
                const newValue = e.detail?.value || e.target?.value;
                setValue(fieldConfig.name, newValue, { shouldValidate: isFormSubmitted });
              } :
              (e: any) => setValue(fieldConfig.name, e.target?.value, { shouldValidate: isFormSubmitted });

            // Get current field value for debugging and key generation
            const currentValue = formValues[fieldConfig.name] || '';

            return (
              <ValidatedField
                key={`${rowIndex}-${fieldConfig.name}`}
                name={fieldConfig.name}
                label={fieldConfig.label}
                type={fieldConfig.type}
                value={currentValue}
                onChangeCustom={handleFieldChange}
                error={errors[fieldConfig.name] || undefined}
                options={fieldOptions}
                placeholder={fieldConfig.placeholder || ''}
                disabled={isDisabled}
                register={register}
                setValue={setValue}
                clearErrors={clearErrors}
                setError={setError}
                validation={fieldConfig.validation}
                limitations={fieldConfig.limitations}
                colSize={fieldConfig.colSize || "12"}
                formValues={formValues}
                trigger={trigger}
                isFormSubmitted={isFormSubmitted}
                {...(fieldConfig.helpText !== undefined && { helpText: fieldConfig.helpText })}
                {...(fieldConfig.maxSelection !== undefined && { maxSelection: fieldConfig.maxSelection })}
                {...(fieldConfig.filter === true && { filter: true })}
                {...(((fieldName === 'vendorSpecialty' || fieldConfig.type === 'multiselecttag' || fieldConfig.type === 'multiselect' || fieldConfig.type === 'select') && fieldConfig.groupBy) && { groupBy: fieldConfig.groupBy })}
                {...(fieldConfig.colSizeSm && { colSizeSm: fieldConfig.colSizeSm })}
                {...(fieldConfig.colSizeMd && { colSizeMd: fieldConfig.colSizeMd })}
                {...(fieldConfig.colSizeLg && { colSizeLg: fieldConfig.colSizeLg })}
              />
            );
          })}
        </div>
      ))}
    </>)
  }

  // Search Form Component rendering
  return (
    <div className="tds-container tds-padding-top-xl tds-padding-left-xl">
      <div className="tds-row">
        <div className="tds-col-11">
          <div
            ref={formRegionRef}
            role="region"
            aria-labelledby="search-form-heading"
            aria-describedby="search-help-text"
            className="tds-margin-bottom-xl"
          >
            <h3 id="search-form-heading" ref={headingRef} className="tds-h2" tabIndex={-1} aria-describedby="search-help-text">
              {formConfig.title}
            </h3>
            <span
              ref={statusLiveRef}
              className="tds-sr-only"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {statusMessage}
            </span>
          </div>
          {/* Error Alert (search failure) */}
          {showErrorAlert && error && (
            <div
              className="tds-margin-bottom-l"
              ref={errorAlertRef}
              tabIndex={-1}
              aria-live="assertive"
              aria-atomic="true"
            >
              <TdsAlert type="error">
                {error}
              </TdsAlert>
            </div>
          )}
          {/* Helper text for admin role */}
          {!isExternalUser && formConfig.helpText && (
              <p id="search-help-text" role="note" className="tds-small tds-margin-top-xl tds-margin-bottom-medium">
                {formConfig.helpText}
              </p>
          )}
          <form
            autoComplete='off'
            onSubmit={(e) => {
              e.preventDefault();
              handleFormSubmit(e);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isLoading) {
                e.preventDefault();
                handleFormSubmit(e);
              }
            }}
            className="tds-margin-bottom-large tds-margin-top-large"
            role="form"
            aria-labelledby="search-form-heading"
            aria-describedby="search-help-text"
            aria-busy={isLoading ? true : undefined}
          >
            {/* Render the form fields element based on the provided formConfig */}
            {genericRenderFields()}
            <div className="tds-row">
              <div className="tds-col-12">
                <div className="tds-margin-top-xxl">
                  <TdsButtonContainer inline inlineAt="800">
                    <TdsButton
                      variant="secondary"
                      disabled={isLoading}
                      aria-label={AUTO_SEARCH_TEXT.SEARCH_BUTTON}
                      aria-describedby="search-help-text"
                      onClick={handleFormSubmit}
                    >
                      {isLoading ? COMMON_TEXT.SEARCHING : AUTO_SEARCH_TEXT.SEARCH_BUTTON}
                    </TdsButton>
                    <TdsButton
                      variant="tertiary"
                      onClick={handleClearSearch}
                      disabled={isLoading}
                      aria-label={AUTO_SEARCH_TEXT.CLEAR_SEARCH}
                      ref={clearButtonRef}
                    >
                      {AUTO_SEARCH_TEXT.CLEAR_SEARCH}
                    </TdsButton>
                  </TdsButtonContainer>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SearchForm;